import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as Sentry from '@sentry/vue'
import { identifyUser, registerSuperProperties, resetUser as resetAnalyticsUser, trackEvent } from '../services/analytics'
import type { AnalyticsUserProperties } from '../types/analytics'
import type { UserProfile } from '../services/api'
import { authCallback, getMe } from '../services/api'
import { isTokenExpired } from '../utils/authErrorHandler'

export const useUserStore = defineStore('user', () => {
    // State
    const user = ref<UserProfile | null>(null)
    const isLoading = ref(false)
    const needsSignup = ref(false)
    const error = ref<string | null>(null)

    // Computed
    const isAuthenticated = computed(() => user.value !== null)
    const role = computed(() => user.value?.role ?? null)
    const isAdmin = computed(() => user.value?.role === 'ADMIN')
    const isAdvisor = computed(() => user.value?.role === 'ADVISOR')
    const isSupplier = computed(() =>
        user.value?.role === 'SUPPLIER_ADMIN' || user.value?.role === 'SUPPLIER_USER'
    )
    const isSupplierAdmin = computed(() => user.value?.role === 'SUPPLIER_ADMIN')
    const isSupplierUser = computed(() => user.value?.role === 'SUPPLIER_USER')
    const isChannelManager = computed(() => user.value?.role === 'CHANNEL_MANAGER')

    // Get the relevant ID for API calls based on role
    const advisorId = computed(() => user.value?.advisor?.id ?? null)
    const supplierId = computed(() => user.value?.supplierEmployee?.supplier?.id ?? null)
    const supplierEmployeeId = computed(() => user.value?.supplierEmployee?.id ?? null)
    const channelManagerId = computed(() => (user.value as any)?.channelManager?.id ?? null)
    const channelManagerOnboarded = computed(() => !!(user.value as any)?.channelManager?.onboardedAt)

    // Tier info
    const advisorTier = computed(() => user.value?.advisor?.tier ?? null)
    const supplierTier = computed(() => user.value?.supplierEmployee?.supplier?.tier ?? null)

    // Supplier self-serve edit rights (CTO, 2026-10-14): FREE is display-only.
    // BASIC+ edits the Company Profile; ENTERPRISE edits products/training.
    // Impersonating admins bypass tiers, same as the backend tier gate.
    const canEditSupplierProfile = computed(() =>
        isImpersonating.value || (isSupplierAdmin.value && supplierTier.value !== null && supplierTier.value !== 'FREE'))
    const canEditSupplierProducts = computed(() =>
        isImpersonating.value || (isSupplierAdmin.value && supplierTier.value === 'ENTERPRISE'))

    // Channel Manager (MDF): supplier employee restricted to MDF pages only.
    // Impersonating admins are never restricted.
    const isMdfOnly = computed(() =>
        !isImpersonating.value && (user.value?.supplierEmployee as any)?.permissionLevel === 'MDF')

    // Advisor's TSD network names (preserve original casing from DB)
    const advisorTsdNames = computed(() =>
        (user.value?.advisor?.tsdNames ?? [])
    )

    // Actions
    async function initUser(token: string, email: string, name: string | null) {
        isLoading.value = true
        error.value = null
        needsSignup.value = false

        try {
            const response = await authCallback(email, name, token)

            if (response.needsSignup) {
                needsSignup.value = true
                user.value = null
                return { needsSignup: true }
            }

            if (response.user) {
                user.value = response.user
                Sentry.setUser({ id: response.user.id, email: response.user.email })
                Sentry.setTag('user.role', response.user.role)
                _identifyAndRegister(response.user)
                return { user: response.user }
            }

            throw new Error('Unexpected response from auth callback')
        } catch (e: any) {
            error.value = e.message || 'Failed to initialize user'
            throw e
        } finally {
            isLoading.value = false
        }
    }

    async function fetchUser(token: string) {
        isLoading.value = true
        error.value = null

        try {
            const response = await getMe(token)

            if (response.needsSignup) {
                needsSignup.value = true
                user.value = null
                return { needsSignup: true }
            }

            if (response.user) {
                user.value = response.user
                Sentry.setUser({ id: response.user.id, email: response.user.email })
                Sentry.setTag('user.role', response.user.role)
                _identifyAndRegister(response.user)
                return { user: response.user }
            }

            throw new Error('Unexpected response from get me')
        } catch (e: any) {
            error.value = e.message || 'Failed to fetch user'
            throw e
        } finally {
            isLoading.value = false
        }
    }

    function setUser(newUser: UserProfile) {
        user.value = newUser
        needsSignup.value = false
        error.value = null
        Sentry.setUser({ id: newUser.id, email: newUser.email })
        Sentry.setTag('user.role', newUser.role)
        _identifyAndRegister(newUser)
    }

    // Quietly re-pull the signed-in user's profile so an admin-granted change —
    // e.g. enabling Sweep (features.sweep) — takes effect in the live session
    // without a full logout/login. Unlike fetchUser this never flips isLoading
    // (so it won't flash app-level loading) and skips needsSignup handling.
    // No-op when signed out or impersonating (don't clobber the puppet session).
    async function refreshProfile(token: string) {
        if (!user.value || isImpersonating.value) return
        try {
            const response = await getMe(token)
            if (response?.user) {
                user.value = response.user
                _identifyAndRegister(response.user)
            }
        } catch {
            // Transient failure — keep the current profile.
        }
    }

    function clearUser() {
        trackEvent('logout')
        user.value = null
        needsSignup.value = false
        error.value = null
        // Drop any active impersonation — otherwise restoreImpersonationFromStorage()
        // resurrects the puppet session for whoever logs in next.
        originalToken.value = null
        localStorage.removeItem('impersonation_token')
        localStorage.removeItem('impersonation_original_token')
        Sentry.setUser(null)
        resetAnalyticsUser()
    }

    /** Identify user in MixPanel and register super properties for all subsequent events. */
    function _identifyAndRegister(u: UserProfile) {
        const tier = u.supplierEmployee?.supplier?.tier ?? u.advisor?.tier ?? null
        const companyName = u.supplierEmployee?.supplier?.name ?? u.advisor?.firmName ?? null
        const onboardingCompleted = !!(
            u.advisor?.onboardingCompletedAt ??
            u.supplierEmployee?.supplier?.onboardingCompletedAt
        )

        const userProps: AnalyticsUserProperties = {
            $email: u.email,
            $name: u.name ?? null,
            role: u.role,
            tier,
            company_name: companyName,
            signup_date: u.createdAt ?? null,
            onboarding_completed: onboardingCompleted,
        }

        identifyUser(u.id, userProps)

        registerSuperProperties({
            role: u.role,
            tier,
            is_impersonating: !!originalToken.value,
            app_version: __APP_VERSION__,
        })
    }

    function getDefaultRoute(): string {
        if (!user.value) return '/'

        switch (user.value.role) {
            case 'ADMIN':
                return '/admin'
            case 'ADVISOR':
                return '/advisor/today'
            case 'SUPPLIER_ADMIN':
            case 'SUPPLIER_USER':
                return '/supplier'
            case 'CHANNEL_MANAGER':
                return '/cm/today'
            default:
                return '/'
        }
    }

    /**
     * Determine where to route the user after login/signup.
     * If onboarding is incomplete, route to the onboarding flow.
     * Otherwise, route to the default dashboard.
     */
    function getOnboardingRoute(): string {
        if (!user.value) return '/'

        if (user.value.role === 'ADVISOR' && user.value.advisor) {
            const { onboardingCompletedAt, onboardingSkippedAt, onboardingStep } = user.value.advisor
            if (!onboardingCompletedAt && !onboardingSkippedAt && onboardingStep) {
                return '/advisor/onboarding'
            }
        }

        if (user.value.role === 'SUPPLIER_ADMIN' && user.value.supplierEmployee?.supplier) {
            const { onboardingCompletedAt, onboardingSkippedAt, onboardingStep } = user.value.supplierEmployee.supplier
            if (!onboardingCompletedAt && !onboardingSkippedAt && onboardingStep) {
                return '/supplier/onboarding'
            }
        }

        return getDefaultRoute()
    }

    // Impersonation state
    const originalToken = ref<string | null>(null)
    const isImpersonating = computed(() => !!originalToken.value)

    function startImpersonation(adminToken: string, impToken: string, impersonatedUser: any) {
        // Save the admin's original token so we can restore later
        originalToken.value = adminToken
        localStorage.setItem('impersonation_original_token', adminToken)

        // Store the impersonation token for API calls
        localStorage.setItem('impersonation_token', impToken)

        // Update user state immediately
        user.value = impersonatedUser
    }

    /**
     * Restore impersonation state from localStorage on app boot / page reload.
     *
     * Without this, after a page reload the customAuthGuard finds the admin's
     * dev-login token and re-fetches the admin user — silently overwriting
     * the impersonation. The impersonation JWT carries the target-user payload,
     * but it's a snapshot from when impersonation started (and lacks fields like
     * branding), so we hydrate from it and then refresh from /auth/me — otherwise
     * edits made while impersonating look lost after a reload.
     *
     * Returns true if a valid impersonation was restored.
     */
    async function restoreImpersonationFromStorage(): Promise<boolean> {
        const impToken = localStorage.getItem('impersonation_token')
        const adminToken = localStorage.getItem('impersonation_original_token')
        if (!impToken || !adminToken) return false

        // Drop expired sessions on the floor — surface the FREE/admin state
        // instead of stale impersonation data.
        if (isTokenExpired(impToken)) {
            localStorage.removeItem('impersonation_token')
            localStorage.removeItem('impersonation_original_token')
            return false
        }

        // Already restored this page load — the in-memory user is the freshest copy
        if (originalToken.value && user.value) return true

        try {
            const parts = impToken.split('.')
            if (parts.length !== 3 || !parts[1]) return false
            // JWT payloads are base64url-encoded (`-`/`_` instead of `+`/`/`).
            // `atob` rejects those characters, so normalize before decoding.
            const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
            const payload = JSON.parse(atob(b64))
            if (!payload?.is_impersonation) return false

            originalToken.value = adminToken
            user.value = payload as UserProfile
        } catch (e) {
            console.warn('[user] Failed to restore impersonation from storage:', e)
            localStorage.removeItem('impersonation_token')
            localStorage.removeItem('impersonation_original_token')
            return false
        }

        try {
            const response = await getMe(impToken)
            if (response.user) user.value = response.user
        } catch (e) {
            console.warn('[user] Failed to refresh impersonated user, using token payload:', e)
        }
        return true
    }

    async function stopImpersonation() {
        if (!originalToken.value) return

        trackEvent('admin_impersonation_stopped')

        originalToken.value = null

        // Clean up
        localStorage.removeItem('impersonation_original_token')
        localStorage.removeItem('impersonation_token')

        // Hard-reload straight to the admin home. We deliberately do NOT re-fetch
        // the admin profile here first: setting it while the router is still on the
        // impersonated (advisor) route flashes the admin's own Sweep nav onto that
        // route for an instant. The reload discards in-memory state anyway and
        // re-hydrates the admin session cleanly on boot, so the fetch was only
        // ever a pre-reload render — the source of the flash.
        window.location.href = '/admin'
    }

    return {
        // State
        user,
        isLoading,
        needsSignup,
        error,
        isImpersonating,

        // Computed
        isAuthenticated,
        role,
        isAdmin,
        isAdvisor,
        isSupplier,
        isSupplierAdmin,
        isMdfOnly,
        isSupplierUser,
        isChannelManager,
        advisorId,
        supplierId,
        supplierEmployeeId,
        channelManagerId,
        channelManagerOnboarded,
        advisorTier,
        supplierTier,
        canEditSupplierProfile,
        canEditSupplierProducts,
        advisorTsdNames,

        // Actions
        initUser,
        fetchUser,
        refreshProfile,
        setUser,
        clearUser,
        getDefaultRoute,
        getOnboardingRoute,
        startImpersonation,
        stopImpersonation,
        restoreImpersonationFromStorage
    }
})
